import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { emptyInputs } from "@/lib/calculations";

const STORAGE_KEY = "hs_worksheet_v1";
const AnalysisContext = createContext(null);
export const useAnalysis = () => useContext(AnalysisContext);

// Exposed so AuthContext can wipe the autosaved worksheet on logout — the
// worksheet contains sensitive financial inputs (income, debts, cash, credit
// range, ZIP). Auth session secrets are NEVER stored here or in localStorage;
// they live only in HttpOnly / Secure cookies. See PRE_LAUNCH_SECURITY_PASS.md.
export function clearStoredWorksheet() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore quota/private-mode errors */ }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const doc = JSON.parse(raw);
    if (!doc || typeof doc !== "object") return null;
    return doc;
  } catch (e) { console.warn("Analysis worksheet load failed", e); return null; }
}

// A worksheet has "user data" if any meaningful input has been entered.
export function hasUserData(inputs) {
  if (!inputs) return false;
  const keys = ["purchase_price", "zip", "annual_taxes", "annual_insurance", "hoa_monthly",
    "interest_rate", "down_payment", "monthly_income", "cash_available", "desired_reserve",
    "seller_credit", "lender_credit", "car_payments", "credit_card_min"];
  for (const k of keys) {
    const v = inputs[k];
    if (v != null && v !== "" && Number(v) !== 0) return true;
  }
  const items = inputs.pre_closing_items || [];
  if (items.some(i => Number(i.amount || 0) > 0)) return true;
  return false;
}

export function AnalysisProvider({ children }) {
  const initial = useRef(loadFromStorage());
  const [inputs, _setInputs] = useState(initial.current?.inputs || emptyInputs());
  const [currentStep, _setCurrentStep] = useState(initial.current?.currentStep || 0);
  const [savedPropertyId, _setSavedPropertyId] = useState(initial.current?.savedPropertyId || null);

  // Persist on any change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ inputs, currentStep, savedPropertyId }));
    } catch (e) { console.warn("Analysis worksheet persist failed", e); }
  }, [inputs, currentStep, savedPropertyId]);

  const setInputs = useCallback((updater) => {
    _setInputs(prev => typeof updater === "function" ? updater(prev) : updater);
  }, []);
  const patchInputs = useCallback((patch) => _setInputs(prev => ({ ...prev, ...patch })), []);
  const setCurrentStep = useCallback((s) => _setCurrentStep(s), []);
  const setSavedPropertyId = useCallback((id) => _setSavedPropertyId(id), []);

  const loadFromProperty = useCallback((property) => {
    if (!property) return;
    _setInputs(property.inputs || emptyInputs());
    _setCurrentStep(0);
    _setSavedPropertyId(property.property_id || null);
  }, []);

  const reset = useCallback(() => {
    _setInputs(emptyInputs());
    _setCurrentStep(0);
    _setSavedPropertyId(null);
    try { localStorage.removeItem(STORAGE_KEY); }
    catch (e) { console.warn("Analysis worksheet reset failed", e); }
  }, []);

  const value = {
    inputs, setInputs, patchInputs,
    currentStep, setCurrentStep,
    savedPropertyId, setSavedPropertyId,
    loadFromProperty, reset,
    hasWorksheet: hasUserData(inputs),
  };
  return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
}
